import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../../ui/dialog';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Save, 
  X,
  Monitor,
  Smartphone,
  Tablet,
  Calendar,
  Clock,
  Target,
  MessageSquare,
  RefreshCw,
  Image as ImageIcon
} from 'lucide-react';
import { API_CONFIG } from '../../../config/api';
import MediaSelector from '../MediaSelector';

const AdminPopup = () => {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    button_text: 'Tamam',
    button_url: '',
    image_url: '',
    image_width: '',
    image_height: '',
    is_active: true,
    show_on_pages: 'all', // all, home, specific
    specific_pages: '',
    start_date: '',
    end_date: '',
    display_frequency: 'once', // once, daily, weekly, always
    target_devices: 'all', // all, desktop, mobile, tablet
    position: 'center', // center, top, bottom
    size: 'medium', // small, medium, large
    background_color: '#ffffff',
    text_color: '#000000',
    button_color: '#3b82f6',
    button_text_color: '#ffffff'
  });

  useEffect(() => {
    fetchPopups();
  }, []);

  const fetchPopups = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getPopups`);
      if (!response.ok) throw new Error('Failed to fetch popups');
      const data = await response.json();
      // Ensure data is always an array
      setPopups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching popups:', error);
      setPopups([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingPopup 
        ? `${API_CONFIG.BASE_URL}/backend/admin_api.php?action=updatePopup&id=${editingPopup.id}`
        : `${API_CONFIG.BASE_URL}/backend/admin_api.php?action=createPopup`;
      
      const method = editingPopup ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save popup');
      
      await fetchPopups();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving popup:', error);
      alert('Popup kaydedilemedi. Lütfen tekrar deneyin.');
    }
  };

  const handleEdit = (popup) => {
    setEditingPopup(popup);
    setFormData({
      title: popup.title || '',
      content: popup.content || '',
      button_text: popup.button_text || 'Tamam',
      button_url: popup.button_url || '',
      image_url: popup.image_url || '',
      is_active: popup.is_active === 1,
      show_on_pages: popup.show_on_pages || 'all',
      specific_pages: popup.specific_pages || '',
      start_date: popup.start_date || '',
      end_date: popup.end_date || '',
      display_frequency: popup.display_frequency || 'once',
      target_devices: popup.target_devices || 'all',
      position: popup.position || 'center',
      size: popup.size || 'medium',
      background_color: popup.background_color || '#ffffff',
      text_color: popup.text_color || '#000000',
      button_color: popup.button_color || '#3b82f6',
      button_text_color: popup.button_text_color || '#ffffff'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu popup\'ı silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=deletePopup&id=${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete popup');
      
      await fetchPopups();
    } catch (error) {
      console.error('Error deleting popup:', error);
      alert('Popup silinemedi. Lütfen tekrar deneyin.');
    }
  };

  const handleResetCache = async () => {
    if (!confirm('Tüm kullanıcıların popup hafızasını sıfırlamak istediğinizden emin misiniz?\n\nBu işlem tüm kullanıcıların popup\'ları tekrar görmesini sağlayacak.')) {
      return;
    }

    try {
      // Backend'e sinyal gönder
      const response = await fetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=resetPopupCache`, {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        // Mevcut kullanıcının localStorage'ını da temizle
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('popup_')) {
            keysToRemove.push(key);
          }
        }
        
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
        });
        
        alert(`Popup cache sıfırlama sinyali gönderildi!\n\n${keysToRemove.length} popup cache kaydı temizlendi.\nTüm kullanıcılar popup\'ları tekrar görebilecek.`);
      } else {
        alert('Cache sıfırlama işlemi başarısız oldu!');
      }
    } catch (error) {
      console.error('Error resetting popup cache:', error);
      alert('Cache sıfırlama işlemi başarısız oldu!');
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      // Mevcut popup'ın tüm verilerini bul
      const popup = popups.find(p => p.id === id);
      if (!popup) return;
      
      // Tüm verileri koru, sadece is_active'i değiştir
      const updatedData = {
        title: popup.title || '',
        content: popup.content || '',
        button_text: popup.button_text || 'Tamam',
        button_url: popup.button_url || '',
        image_url: popup.image_url || '',
        image_width: popup.image_width || '',
        image_height: popup.image_height || '',
        is_active: !currentStatus,
        show_on_pages: popup.show_on_pages || 'all',
        specific_pages: popup.specific_pages || '',
        start_date: popup.start_date || '',
        end_date: popup.end_date || '',
        display_frequency: popup.display_frequency || 'once',
        target_devices: popup.target_devices || 'all',
        position: popup.position || 'center',
        size: popup.size || 'medium',
        background_color: popup.background_color || '#ffffff',
        text_color: popup.text_color || '#000000',
        button_color: popup.button_color || '#3b82f6',
        button_text_color: popup.button_text_color || '#ffffff'
      };
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=updatePopup&id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData)
      });
      
      if (!response.ok) throw new Error('Failed to update popup');
      
      await fetchPopups();
    } catch (error) {
      console.error('Error updating popup:', error);
      alert('Popup durumu güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      button_text: 'Tamam',
      button_url: '',
      image_url: '',
      image_width: '',
      image_height: '',
      is_active: true,
      show_on_pages: 'all',
      specific_pages: '',
      start_date: '',
      end_date: '',
      display_frequency: 'once',
      target_devices: 'all',
      position: 'center',
      size: 'medium',
      background_color: '#ffffff',
      text_color: '#000000',
      button_color: '#3b82f6',
      button_text_color: '#ffffff'
    });
    setEditingPopup(null);
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge className="bg-green-100 text-green-800">Aktif</Badge>
    ) : (
      <Badge className="bg-gray-100 text-gray-800">Pasif</Badge>
    );
  };

  const getActivePopupCount = () => {
    return Array.isArray(popups) ? popups.filter(popup => popup.is_active === 1).length : 0;
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'desktop': return <Monitor className="w-4 h-4" />;
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Popup Yönetimi</h1>
          <p className="text-gray-600 mt-2">Site popup\'larını yönetin ve düzenleyin</p>
          <div className="mt-2 flex items-center space-x-4">
            <Badge className="bg-blue-100 text-blue-800">
              Toplam: {Array.isArray(popups) ? popups.length : 0}
            </Badge>
            <Badge className="bg-green-100 text-green-800">
              Aktif: {getActivePopupCount()}
            </Badge>
            {getActivePopupCount() > 1 && (
              <Badge className="bg-red-100 text-red-800">
                ⚠️ Birden fazla aktif popup!
              </Badge>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleResetCache}
            variant="outline"
            className="flex items-center space-x-2 text-orange-600 border-orange-200 hover:bg-orange-50"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Cache Sıfırla</span>
          </Button>
        </div>
      </div>
      
      <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Popup
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPopup ? 'Popup Düzenle' : 'Yeni Popup Oluştur'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Temel Bilgiler */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Başlık (opsiyonel)</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Popup başlığı"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="button_text">Buton Metni</Label>
                  <Input
                    id="button_text"
                    value={formData.button_text}
                    onChange={(e) => setFormData({...formData, button_text: e.target.value})}
                    placeholder="Tamam"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">İçerik (opsiyonel)</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Popup içeriği (HTML desteklenir)"
                  rows={4}
                />
                <p className="text-xs text-gray-500">
                  HTML etiketleri kullanabilirsiniz. Örn: &lt;p&gt;, &lt;strong&gt;, &lt;br/&gt;
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="button_url">Buton URL (opsiyonel)</Label>
                <Input
                  id="button_url"
                  value={formData.button_url}
                  onChange={(e) => setFormData({...formData, button_url: e.target.value})}
                  placeholder="https://example.com"
                />
              </div>

              <div className="space-y-2">
                <MediaSelector
                  value={formData.image_url}
                  onChange={(url) => setFormData({...formData, image_url: url})}
                  label="Popup Resmi (opsiyonel)"
                  placeholder="Medya kütüphanesinden resim seçin veya URL girin..."
                />
                {formData.image_url && (
                  <p className="text-xs text-blue-600 mt-1">
                    ℹ️ Resim popup'ın üst kısmında gösterilir, altında yazılar görünür
                  </p>
                )}
              </div>

              {/* Resim Boyutları */}
              {formData.image_url && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="image_width">Resim Genişliği</Label>
                    <Input
                      id="image_width"
                      value={formData.image_width}
                      onChange={(e) => setFormData({...formData, image_width: e.target.value})}
                      placeholder="Örn: 100%, 500px, auto"
                    />
                    <p className="text-xs text-gray-500">
                      Piksel (px), yüzde (%) veya auto girebilirsiniz
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="image_height">Resim Yüksekliği</Label>
                    <Input
                      id="image_height"
                      value={formData.image_height}
                      onChange={(e) => setFormData({...formData, image_height: e.target.value})}
                      placeholder="Örn: 300px, 50%, auto"
                    />
                    <p className="text-xs text-gray-500">
                      Piksel (px), yüzde (%) veya auto girebilirsiniz
                    </p>
                  </div>
                </div>
              )}

              {/* Görünüm Ayarları */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Görünüm Ayarları</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="position">Pozisyon</Label>
                    <select
                      id="position"
                      value={formData.position}
                      onChange={(e) => setFormData({...formData, position: e.target.value})}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="center">Merkez</option>
                      <option value="top">Üst</option>
                      <option value="bottom">Alt</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="size">Boyut</Label>
                    <select
                      id="size"
                      value={formData.size}
                      onChange={(e) => setFormData({...formData, size: e.target.value})}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="small">Küçük</option>
                      <option value="medium">Orta</option>
                      <option value="large">Büyük</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="target_devices">Hedef Cihazlar</Label>
                    <select
                      id="target_devices"
                      value={formData.target_devices}
                      onChange={(e) => setFormData({...formData, target_devices: e.target.value})}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="all">Tümü</option>
                      <option value="desktop">Masaüstü</option>
                      <option value="mobile">Mobil</option>
                      <option value="tablet">Tablet</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Renk Ayarları */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Renk Ayarları</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="background_color">Arka Plan</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="background_color"
                        type="color"
                        value={formData.background_color}
                        onChange={(e) => setFormData({...formData, background_color: e.target.value})}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={formData.background_color}
                        onChange={(e) => setFormData({...formData, background_color: e.target.value})}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="text_color">Metin Rengi</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="text_color"
                        type="color"
                        value={formData.text_color}
                        onChange={(e) => setFormData({...formData, text_color: e.target.value})}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={formData.text_color}
                        onChange={(e) => setFormData({...formData, text_color: e.target.value})}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="button_color">Buton Rengi</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="button_color"
                        type="color"
                        value={formData.button_color}
                        onChange={(e) => setFormData({...formData, button_color: e.target.value})}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={formData.button_color}
                        onChange={(e) => setFormData({...formData, button_color: e.target.value})}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="button_text_color">Buton Metin Rengi</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="button_text_color"
                        type="color"
                        value={formData.button_text_color}
                        onChange={(e) => setFormData({...formData, button_text_color: e.target.value})}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={formData.button_text_color}
                        onChange={(e) => setFormData({...formData, button_text_color: e.target.value})}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Zamanlama Ayarları */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Zamanlama Ayarları</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                    <Input
                      id="start_date"
                      type="datetime-local"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">Bitiş Tarihi</Label>
                    <Input
                      id="end_date"
                      type="datetime-local"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label htmlFor="display_frequency">Görüntüleme Sıklığı</Label>
                  <select
                    id="display_frequency"
                    value={formData.display_frequency}
                    onChange={(e) => setFormData({...formData, display_frequency: e.target.value})}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="once">Sadece Bir Kez</option>
                    <option value="daily">Günlük</option>
                    <option value="weekly">Haftalık</option>
                    <option value="always">Her Zaman</option>
                  </select>
                </div>
              </div>

              {/* Sayfa Ayarları */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Sayfa Ayarları</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="show_on_pages">Gösterilecek Sayfalar</Label>
                    <select
                      id="show_on_pages"
                      value={formData.show_on_pages}
                      onChange={(e) => setFormData({...formData, show_on_pages: e.target.value})}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="all">Tüm Sayfalar</option>
                      <option value="home">Sadece Ana Sayfa</option>
                      <option value="specific">Belirli Sayfalar</option>
                    </select>
                  </div>
                  {formData.show_on_pages === 'specific' && (
                    <div className="space-y-2">
                      <Label htmlFor="specific_pages">Sayfa URL'leri (virgülle ayırın)</Label>
                      <Input
                        id="specific_pages"
                        value={formData.specific_pages}
                        onChange={(e) => setFormData({...formData, specific_pages: e.target.value})}
                        placeholder="/about, /contact, /services"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Durum */}
              <div className="border-t pt-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                  <Label htmlFor="is_active">Popup Aktif</Label>
                </div>
                {formData.is_active && (
                  <p className="text-sm text-amber-600 mt-2">
                    ⚠️ Bu popup aktif yapıldığında diğer tüm popup'lar otomatik olarak pasif yapılacak.
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-6">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  <X className="w-4 h-4 mr-2" />
                  İptal
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" />
                  {editingPopup ? 'Güncelle' : 'Oluştur'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      {/* Popup Listesi */}
      <div className="grid gap-6">
        {!Array.isArray(popups) || popups.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Henüz popup yok</h3>
              <p className="text-gray-600 mb-4">İlk popup\'ınızı oluşturmak için yukarıdaki butona tıklayın.</p>
            </CardContent>
          </Card>
        ) : (
          Array.isArray(popups) && popups.map((popup) => (
            <Card key={popup.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{popup.title}</h3>
                      {getStatusBadge(popup.is_active)}
                      <div className="flex items-center space-x-1 text-gray-500">
                        {getDeviceIcon(popup.target_devices)}
                        <span className="text-sm capitalize">{popup.target_devices}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-3 line-clamp-2">{popup.content}</p>
                    {popup.image_url && (
                      <div className="mb-3">
                        <img 
                          src={popup.image_url.startsWith('http') ? popup.image_url : 
                               popup.image_url.includes('screenshot_') ? 
                               `${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getScreenshot&file=${popup.image_url.split('/').pop()}` :
                               `${API_CONFIG.BASE_URL}${popup.image_url}`}
                          alt="Popup resmi"
                          className="w-24 h-16 object-cover rounded border"
                        />
                        <p className="text-xs text-blue-600 mt-1">Resim popup'ı</p>
                      </div>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Target className="w-4 h-4" />
                        <span className="capitalize">{popup.position}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span className="capitalize">{popup.display_frequency}</span>
                      </div>
                      {popup.start_date && (
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(popup.start_date).toLocaleDateString('tr-TR')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleActive(popup.id, popup.is_active)}
                    >
                      {popup.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(popup)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(popup.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminPopup;
